import { useEffect, useState } from 'react'
import {
	Easing,
	ReduceMotion,
	runOnJS,
	useAnimatedReaction,
	useSharedValue,
	withDelay,
	withRepeat,
	withSequence,
	withTiming,
} from 'react-native-reanimated'

const DURATION = 1400
const DELAY = 200

/**
 * A number that arrives rather than appears: it counts from nothing to its value over a second
 * and a half, easing out. `shown` is the number to render; `progress` is the same run as a shared
 * value, for anything that should move with it — an arc, a bar. Honours Reduce Motion, where the
 * value simply lands.
 */
export const useCountUp = (to: number, duration = DURATION) => {
	const progress = useSharedValue(0)
	const [shown, setShown] = useState(0)

	useEffect(() => {
		progress.value = withDelay(
			DELAY,
			withTiming(to, {
				duration,
				easing: Easing.out(Easing.cubic),
				reduceMotion: ReduceMotion.System,
			})
		)
	}, [progress, to, duration])

	useAnimatedReaction(
		() => Math.round(progress.value),
		(value, previous) => {
			if (value !== previous) runOnJS(setShown)(value)
		}
	)

	return { shown, progress }
}

const HOLD = 3000

/**
 * A number that will not settle: it eases to a value, rests there, then moves to the next and
 * keeps going. For the waiting card, where the figure is a stand-in rather than a reading — it
 * has to behave like a score without ever being one.
 */
export const useCountCycle = (values: number[], duration = DURATION, hold = HOLD) => {
	const progress = useSharedValue(0)
	const [shown, setShown] = useState(0)

	useEffect(() => {
		const legs = values.flatMap((value) => [
			withTiming(value, {
				duration,
				easing: Easing.inOut(Easing.cubic),
				reduceMotion: ReduceMotion.System,
			}),
			// Rest on the value before moving off it.
			withDelay(hold, withTiming(value, { duration: 0 })),
		])
		progress.value = withDelay(DELAY, withRepeat(withSequence(...legs), -1))
	}, [progress, values, duration, hold])

	useAnimatedReaction(
		() => Math.round(progress.value),
		(value, previous) => {
			if (value !== previous) runOnJS(setShown)(value)
		}
	)

	return { shown, progress }
}
